import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { observer } from 'mobx-react';
import { useAuth0 } from '@auth0/auth0-react';
import AuthStore from 'src/stores/AuthStore';
import UserStore from 'src/stores/UserStore';
import OrgStore from 'src/stores/OrgStore';
import FilterStore from 'src/stores/FilterStore';
import EncounterStore from 'src/stores/EncounterStore';
import request, { RequestOrganization } from 'src/api/utils';
import StartupLoading from 'src/components/StartupLoading';
import MfaGate from 'src/components/MfaGate';
import LoadingIcon from 'src/components/LoadingIcon';
import Button from 'src/components/Button';
import Card from 'src/components/Card';
import { ROUTES } from 'src/utils/constants';
import { validateRoute } from 'src/utils/roleValidation';
import { getMfaToken, setMfaToken } from 'src/utils/mfaToken';
import Encounters from 'src/containers/Encounters';
import { encounterLinkFilters, isValidDestination, OpenDestination, OpenTarget, parseOpenLink } from './link';
import styles from './styles.module.scss';

function OpenLinkMessage({
    children,
    retry,
    onLogout,
}: {
    children: React.ReactNode;
    retry?: () => void;
    onLogout?: () => void;
}) {
    const history = useHistory();
    return (
        <div className={styles.page}>
            <Card label="Open shared link">
                <div className={styles.body}>
                    <div role="alert" className={styles.message}>
                        {children}
                    </div>
                    <div className={styles.actions}>
                        {retry && <Button type="primary" text="Retry" onClick={retry} />}
                        {onLogout && <Button type="primary" text="Log out and sign in" onClick={onLogout} />}
                        <Button
                            type="secondary"
                            text="Back to dashboard"
                            onClick={() => history.replace(ROUTES.Home)}
                        />
                    </div>
                </div>
            </Card>
        </div>
    );
}

function RetryAfterMfa({ retry }: { retry: () => void }) {
    useEffect(() => {
        retry();
    }, []);
    return <LoadingIcon />;
}

function EncounterDestination({ destination }: { destination: OpenDestination }) {
    const filterStore = useContext(FilterStore);
    const encounterStore = useContext(EncounterStore);
    const [ready, setReady] = useState(false);
    useEffect(() => {
        encounterStore.reset();
        filterStore.applyLinkFilters(
            'encountersPage',
            encounterLinkFilters(destination, filterStore.encountersPage.pagination.limit),
        );
        setReady(true);
        // Editing the filters keeps the user's edit; otherwise leaving the link restores their own filters.
        return () => filterStore.restoreLinkFilters('encountersPage');
    }, [destination, filterStore, encounterStore]);
    return ready ? <Encounters autoSearch /> : <LoadingIcon />;
}

const ResolveOpenLink = observer(
    ({
        target,
        switchedOrg,
        onLogout,
    }: {
        target: OpenTarget;
        switchedOrg: React.MutableRefObject<string | null>;
        onLogout: () => void;
    }) => {
        const authStore = useContext(AuthStore);
        const userStore = useContext(UserStore);
        const orgStore = useContext(OrgStore);
        const history = useHistory();
        const selectedOrgName = userStore.selectedOrg?.orgNmIntrnl;
        const targetOrg = orgStore.orgs.find((org) => org.orgNmIntrnl === target.orgNameInternal);
        const organization: RequestOrganization = useMemo(
            () => ({
                orgId: targetOrg?.orgId,
                orgNmIntrnl: target.orgNameInternal,
            }),
            [targetOrg?.orgId, target.orgNameInternal],
        );
        const wrongStartupOrg = switchedOrg.current && selectedOrgName !== switchedOrg.current;
        const access = useQuery<OpenDestination, any>({
            // This request has an explicit org scope; selecting its authorized org does not invalidate it.
            queryKey: ['openLink', userStore.usrNm, target.orgNameInternal, target.kind, target.id],
            queryFn: async () => {
                const token = authStore.accessToken;
                const username = userStore.usrNm;
                const response = await request({
                    method: 'get',
                    url: 'encounter/access',
                    organization,
                    params: target.kind === 'forms' ? { encounterFormId: target.id } : { encounterId: target.id },
                });
                if (
                    !authStore.isAuthenticated ||
                    authStore.accessToken !== token ||
                    userStore.usrNm !== username ||
                    userStore.selectedOrg?.orgNmIntrnl !== selectedOrgName
                ) {
                    throw new Error('Navigation changed during access check');
                }
                if (!isValidDestination(response.data, target)) throw new Error('Invalid encounter destination');
                return response.data;
            },
            enabled: Boolean(authStore.accessToken) && !authStore.hasNoActiveOrgs && !wrongStartupOrg,
            retry: false,
            cacheTime: 0,
            staleTime: 0,
            refetchOnWindowFocus: false,
        });
        const destination = access.data;

        useEffect(() => {
            if (!destination || access.error || wrongStartupOrg || !authStore.isAuthenticated || switchedOrg.current)
                return;
            // A cold link has not loaded the org list yet.
            // Preserve verification performed using the target's internal-name scope.
            const token = getMfaToken(organization.orgId || organization.orgNmIntrnl);
            if (token) setMfaToken(token, destination.organization.orgId);
            if (
                selectedOrgName !== target.orgNameInternal ||
                authStore.isLoadingStartupData ||
                !userStore.userPermissions.hasBeenSet
            ) {
                switchedOrg.current = target.orgNameInternal;
                userStore.changeOrg(
                    {
                        orgId: destination.organization.orgId,
                        orgNmIntrnl: destination.organization.orgNameInternal,
                        orgNm: destination.organization.orgName,
                    },
                    history,
                    true,
                );
            }
        }, [
            destination,
            access.error,
            selectedOrgName,
            target.orgNameInternal,
            wrongStartupOrg,
            authStore,
            authStore.isLoadingStartupData,
            userStore,
            userStore.userPermissions.hasBeenSet,
            history,
            organization,
            switchedOrg,
        ]);

        const denied = (
            <OpenLinkMessage onLogout={onLogout}>
                Your current account cannot access this {target.kind === 'forms' ? 'form' : 'encounter'}. Sign in with
                an account that has access to its organization and facility. The record may also be unavailable.
            </OpenLinkMessage>
        );
        if (authStore.hasNoActiveOrgs) return denied;
        if (wrongStartupOrg)
            return (
                <OpenLinkMessage onLogout={onLogout}>
                    Unable to select the linked organization. Please sign in again.
                </OpenLinkMessage>
            );
        if (access.error?.response?.data?.code === 'MFA_REQUIRED') {
            return (
                <MfaGate organization={organization} onLogout={onLogout}>
                    <RetryAfterMfa
                        retry={() => {
                            access.refetch();
                        }}
                    />
                </MfaGate>
            );
        }
        if (access.error) {
            if ([401, 403, 404].includes(access.error.response?.status)) return denied;
            return (
                <OpenLinkMessage
                    retry={() => {
                        access.refetch();
                    }}
                    onLogout={onLogout}
                >
                    Unable to open this link right now. Please try again.
                </OpenLinkMessage>
            );
        }
        if (!destination || selectedOrgName !== target.orgNameInternal) return <LoadingIcon />;
        // Run startup only after preflight selects the authorized destination, never for the
        // default org that AuthStore temporarily selects while restoring the login.
        if (authStore.isLoadingStartupData) {
            return switchedOrg.current ? <StartupLoading>{null}</StartupLoading> : <LoadingIcon />;
        }
        const route = target.kind === 'forms' ? ROUTES.ViewEncounterForm : ROUTES.Encounters;
        if (!userStore.userPermissions.hasBeenSet && !switchedOrg.current) return <LoadingIcon />;
        if (!userStore.userPermissions.hasBeenSet || !validateRoute(route)) return denied;
        if (target.kind === 'forms') {
            return (
                <Redirect
                    to={`/${target.orgNameInternal}/patients/forms/${destination.facility.facilityId}/${target.id}`}
                />
            );
        }
        if (!target.encounterDestination) {
            return <Redirect to={`/${target.orgNameInternal}/patients/encounters?openEncounterId=${target.id}`} />;
        }
        return <EncounterDestination destination={destination} />;
    },
);

function OpenLink() {
    const authStore = useContext(AuthStore);
    const auth0 = useAuth0();
    const history = useHistory();
    const location = useLocation();
    const target = parseOpenLink(location);
    // Keep the authorized selection across startup renders and detect a server-side fallback.
    const switchedOrg = useRef<string | null>(null);
    function signInAgain() {
        const from = target
            ? { pathname: `/open/${target.orgNameInternal}/${target.kind}/${target.id}`, search: '' }
            : location;
        history.replace(ROUTES.Login, { from, forceLogin: true });
        authStore.logout();
    }
    if (!target) return <OpenLinkMessage>This link is invalid. Please check the link and try again.</OpenLinkMessage>;
    // App transfers a restored Auth0 session into AuthStore in an effect, after this render.
    if (auth0.isLoading || (auth0.isAuthenticated && !authStore.isAuthenticated)) return <LoadingIcon />;
    if (!authStore.isAuthenticated) return <Redirect to={{ pathname: ROUTES.Login, state: { from: location } }} />;
    return <ResolveOpenLink target={target} switchedOrg={switchedOrg} onLogout={signInAgain} />;
}

export default observer(OpenLink);
